export type BeatLineTokenContext = {
  state: Record<string, any>;
  online?: Record<string, any>;
  songMetaCache?: Record<string, any>;
  t?: (key: string, fallback?: string) => string;
  alert?: (message: string) => void;
  cleanupPendingCard?: () => void;
  fetchSongMeta?: (ids: unknown[]) => Promise<any>;
  showBonusModal?: (headerText: string, subMsg: string, status: string, opts?: Record<string, any>) => void;
  updateUI?: (skipTimelineRebuild?: boolean) => void;
  stopMusic?: () => void;
  drawCard?: () => void | Promise<void>;
  autoPlaceCard?: (player: number, card: Record<string, any>) => void;
  updateScores?: () => void;
  endGame?: (winner: number) => void;
  beatlineHeardMarkSeen?: (id: unknown) => void;
};

export type BeatLineTokenResult = { handled: boolean; reason?: string };

function safeCall(fn: unknown, ...args: any[]): void {
  if (typeof fn !== 'function') return;
  try { fn(...args); } catch (_) {}
}

async function safeAsyncCall(fn: unknown, ...args: any[]): Promise<void> {
  if (typeof fn !== 'function') return;
  try { await fn(...args); } catch (_) {}
}

function t(ctx: BeatLineTokenContext, key: string, fallback = key): string {
  return typeof ctx.t === 'function' ? ctx.t(key, fallback) : fallback;
}

function normalOfflineOnly(ctx: BeatLineTokenContext): boolean {
  const state = ctx.state || {};
  const online = ctx.online || {};
  if (online && online.enabled) return false;
  return !(state.perfectOrder || state.timeAttack || state.survival || state.speedrun || state.gameEnded);
}

function currentPlayer(ctx: BeatLineTokenContext): Record<string, any> {
  const state = ctx.state;
  const key = `p${parseInt(String(state.turn || 1), 10) || 1}`;
  if (!state[key] || typeof state[key] !== 'object') state[key] = { score: 0, tokens: 0, timeline: [] };
  return state[key];
}

function hideTokenQuestionAreas(): { q: HTMLElement | null; tokenBtns: HTMLElement | null; taBtns: HTMLElement | null } {
  const q = document.getElementById('bonus-token-question');
  const tokenBtns = document.getElementById('bonus-token-btns');
  const taBtns = document.getElementById('bonus-timeattack-btns');
  if (q) q.style.display = 'none';
  if (tokenBtns) tokenBtns.style.display = 'none';
  if (taBtns) taBtns.style.display = 'none';
  return { q, tokenBtns, taBtns };
}

function restoreTokenQuestionAreas(els: { q: HTMLElement | null; tokenBtns: HTMLElement | null; taBtns: HTMLElement | null }): void {
  if (els.q) els.q.style.display = '';
  if (els.tokenBtns) els.tokenBtns.style.display = '';
  if (els.taBtns) els.taBtns.style.display = 'none';
}

export async function actionBuyCardFromTypeScript(ctx: BeatLineTokenContext): Promise<BeatLineTokenResult> {
  if (!normalOfflineOnly(ctx)) return { handled: false, reason: 'complex-or-online' };

  const state = ctx.state;
  const p = currentPlayer(ctx);
  const tokens = parseInt(String(p.tokens || 0), 10) || 0;
  if (tokens < 1 || !state.activeCard) {
    const message = t(ctx, 'alert_not_enough_tokens', 'Niet genoeg tokens');
    if (typeof ctx.alert === 'function') ctx.alert(message); else window.alert(message);
    return { handled: true, reason: 'not-enough-tokens-or-card' };
  }

  const hidden = hideTokenQuestionAreas();
  try {
    if (state.activeCard.id && (!state.activeCard.title || !state.activeCard.year)) {
      await safeAsyncCall(ctx.fetchSongMeta, [state.activeCard.id]);
      if (ctx.songMetaCache && ctx.songMetaCache[state.activeCard.id]) state.activeCard = ctx.songMetaCache[state.activeCard.id];
    }

    const card = state.activeCard;
    if (card && (card.year || card.title) && typeof ctx.showBonusModal === 'function') {
      await new Promise<void>((resolve) => {
        const header = (window as any).beatlineLang === 'en' ? 'SWAPPED' : 'GEWISSELD';
        const msg = (window as any).beatlineLang === 'en' ? 'Correct answer:' : 'Juiste antwoord:';
        ctx.showBonusModal?.(header, msg, 'info', {
          enableSuspense: false,
          modalClass: 'modal-swap',
          autoCloseMs: 3500,
          onAutoClose: resolve,
        });
      });
    }
  } finally {
    restoreTokenQuestionAreas(hidden);
  }

  safeCall(ctx.cleanupPendingCard);
  p.tokens = Math.max(0, tokens - 1);
  safeCall(ctx.updateUI);
  safeCall(ctx.stopMusic);
  await safeAsyncCall(ctx.drawCard);

  return { handled: true, reason: 'swap-card' };
}

export async function actionFreeCardFromTypeScript(ctx: BeatLineTokenContext): Promise<BeatLineTokenResult> {
  if (!normalOfflineOnly(ctx)) return { handled: false, reason: 'complex-or-online' };

  const state = ctx.state;
  const p = currentPlayer(ctx);
  const tokens = parseInt(String(p.tokens || 0), 10) || 0;
  if (tokens < 3) {
    const message = t(ctx, 'alert_not_enough_tokens', 'Niet genoeg tokens');
    if (typeof ctx.alert === 'function') ctx.alert(message); else window.alert(message);
    return { handled: true, reason: 'not-enough-tokens' };
  }

  safeCall(ctx.cleanupPendingCard);
  p.tokens = Math.max(0, tokens - 3);
  safeCall(ctx.stopMusic);

  const deckZone = document.getElementById('deck-zone');
  const checkBtn = document.getElementById('btn-check');
  const nextBtn = document.getElementById('btn-next');
  if (deckZone) deckZone.innerHTML = '';
  if (checkBtn) checkBtn.style.display = 'none';
  if (nextBtn) nextBtn.style.display = 'flex';

  let cardObj = state.activeCard || null;
  if (!cardObj && Array.isArray(state.deck) && state.deck.length > 0) {
    cardObj = state.deck.pop();
    if (cardObj && cardObj.id) safeCall(ctx.beatlineHeardMarkSeen, cardObj.id);
  }
  if (!cardObj) return { handled: true, reason: 'no-card' };

  const id = typeof cardObj === 'string' ? cardObj : (cardObj.id || '');
  if (!id) return { handled: true, reason: 'missing-card-id' };

  await safeAsyncCall(ctx.fetchSongMeta, [id]);
  const meta = (ctx.songMetaCache && ctx.songMetaCache[id]) || { id, title: '', year: 0 };
  state.activeCard = meta;

  const turn = parseInt(String(state.turn || 1), 10) || 1;
  safeCall(ctx.autoPlaceCard, turn, meta);
  safeCall(ctx.updateScores);

  const score = parseInt(String(p.score || 0), 10) || 0;
  const winTarget = parseInt(String(state.winTarget || 10), 10) || 10;
  if (score >= winTarget) safeCall(ctx.endGame, turn);
  else await safeAsyncCall(ctx.drawCard ? undefined : undefined); // no-op: nextTurn is performed by legacy fallback if this branch is not owned

  // Match original free-card flow: after autoPlace + score update it advances to the next turn.
  if (score < winTarget) {
    const nextTurn = (window as any).beatlineTsNextTurn ? undefined : undefined;
    // Use public window function if available through the golden runtime.
    if (typeof (window as any).__beatlineLegacyNextTurnPublic === 'function') {
      (window as any).__beatlineLegacyNextTurnPublic();
    } else if (typeof (window as any).beatlineTsNextTurn === 'function') {
      (window as any).beatlineTsNextTurn({ state, updateUI: ctx.updateUI });
    }
  }

  return { handled: true, reason: 'free-card' };
}
